import React, { useCallback, useState } from 'react';
import InputWrapper from 'Views/Components/Inputs/InputWrapper';
import { Button, Display } from 'Views/Components/Button/Button';
import classnames from 'classnames';
import { Select } from 'Views/Components/Select/Select';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { AvailableCourseFilters } from 'Views/Pages/Frontend/StudyPlanWizard/SearchAndFilterUtilities';
import { Model } from 'Models/Model';
import { sortTermsAndGroupByStudyPeriod } from 'Util/TermUtils';
import { capitalize, toLower } from 'lodash';

export interface SearchAndFilterState {
	searchPhrase: string,
	selectedLevelId: string,
	selectedTermId: string,
	selectedFacultyId: string,
	selectedSchoolId: string,
	selectedDisciplineId: string,
	selectedLocationId: string,
}

interface ICourseSelectionFilterInput {
	searchAndFilterState: SearchAndFilterState;
	availableCourseFilters: AvailableCourseFilters;
	onSearchAndFilter: (value: SearchAndFilterState) => void
}

const CourseSelectionFilterInput = observer((props: ICourseSelectionFilterInput) => {
	const {
		searchAndFilterState,
		availableCourseFilters,
		onSearchAndFilter,
	} = props;
	const [filtersVisible, setFilterVisibility] = useState(false);

	const resetFilters = () => {
		runInAction(() => {
			searchAndFilterState.selectedLevelId = '';
			searchAndFilterState.selectedTermId = '';
			searchAndFilterState.selectedFacultyId = '';
			searchAndFilterState.selectedSchoolId = '';
			searchAndFilterState.selectedDisciplineId = '';
			searchAndFilterState.selectedLocationId = '';
		});

		onSearchAndFilter(searchAndFilterState);
	};

	const toggleFilterVisibility = useCallback((showFilters: boolean) => {
		if (showFilters) {
			setFilterVisibility(true);
		} else {
			setFilterVisibility(false);
			runInAction(() => {
				searchAndFilterState.searchPhrase = '';
			});
		}
	}, [searchAndFilterState]);

	const onSearch = (searchPhrase: string) => {
		runInAction(() => {
			searchAndFilterState.searchPhrase = searchPhrase;
		});

		onSearchAndFilter(searchAndFilterState);
	};

	const modelToComboboxOption = (model: Model) => {
		return {
			value: model.id,
			display: model.getDisplayName(),
		};
	};

	const modelsToComboboxOptions = (models: Model[]) => {
		return models
			.map(modelToComboboxOption)
			.sort((a, b) => a.display.localeCompare(b.display));
	};

	const sortedTerms = availableCourseFilters
		.terms
		.sort(sortTermsAndGroupByStudyPeriod);

	return (
		<div className="selection-actions">
			<div className="selection-actions__row course-search">
				<InputWrapper className="search-course-input-wrapper">
					<span className="icon-left icon-search" />
					<input
						type="text"
						aria-label="Search course by name or code"
						placeholder={`Search ${toLower(window.lexicon.course)} by ${toLower(window.lexicon.title)} or ${toLower(window.lexicon.code)}`}
						value={searchAndFilterState.searchPhrase}
						onChange={e => {
							onSearch(e.target.value);
						}}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								onSearch(searchAndFilterState.searchPhrase);
							}
						}}
					/>
				</InputWrapper>
				<Button
					onClick={() => toggleFilterVisibility(!filtersVisible)}
					className={classnames(
						'filter-btn btn icon-filter-list icon-right',
						{
							'btn--solid': filtersVisible,
							'btn--outline': !filtersVisible,
						},
					)}
				>
					Filter
				</Button>
			</div>
			{filtersVisible ? (
				<div className={classnames('selection-actions__row course-filters', { hidden: !filtersVisible })}>
					<div className="course-filters-inputs">
						<Select<SearchAndFilterState, string>
							label="Level"
							placeholder="Level"
							labelVisible={false}
							options={modelsToComboboxOptions(availableCourseFilters.levels)}
							modelProperty="selectedLevelId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': 'Level',
							}}
						/>
						<Select<SearchAndFilterState, string>
							label={capitalize(window.lexicon.term)}
							placeholder={capitalize(window.lexicon.term)}
							labelVisible={false}
							options={sortedTerms.map(modelToComboboxOption)}
							modelProperty="selectedTermId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': capitalize(window.lexicon.term),
							}}
						/>
						<Select<SearchAndFilterState, string>
							label={capitalize(window.lexicon.faculty)}
							placeholder={capitalize(window.lexicon.faculty)}
							labelVisible={false}
							options={modelsToComboboxOptions(availableCourseFilters.faculties)}
							modelProperty="selectedFacultyId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': capitalize(window.lexicon.faculty),
							}}
						/>
						<Select<SearchAndFilterState, string>
							label={capitalize(window.lexicon.school)}
							placeholder={capitalize(window.lexicon.school)}
							labelVisible={false}
							options={modelsToComboboxOptions(availableCourseFilters.schools)}
							modelProperty="selectedSchoolId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': capitalize(window.lexicon.school),
							}}
						/>
						<Select<SearchAndFilterState, string>
							label={capitalize(window.lexicon.discipline)}
							placeholder={capitalize(window.lexicon.discipline)}
							labelVisible={false}
							options={modelsToComboboxOptions(availableCourseFilters.disciplines)}
							modelProperty="selectedDisciplineId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': capitalize(window.lexicon.discipline),
							}}
						/>
						<Select<SearchAndFilterState, string>
							label={capitalize(window.lexicon.location)}
							placeholder={capitalize(window.lexicon.location)}
							labelVisible={false}
							options={modelsToComboboxOptions(availableCourseFilters.locations)}
							modelProperty="selectedLocationId"
							model={searchAndFilterState}
							searchable={false}
							onAfterChange={() => onSearchAndFilter(searchAndFilterState)}
							inputProps={{
								'aria-label': capitalize(window.lexicon.location),
							}}
						/>
					</div>
					<Button
						className="btn--icon-only clear-filters-btn"
						display={Display.Text}
						icon={{
							icon: 'close',
							iconPos: 'icon-right',
						}}
						onClick={() => resetFilters()}
					/>
				</div>
			) : null}
		</div>
	);
});

export default CourseSelectionFilterInput;
